import React, { useEffect, useContext, useState } from 'react'
import { Box } from '@mui/material'

import LanguageContext from "../../../contexts/LanguageContext"
import UserContext, { getPointsByQuizz } from "../../../contexts/UserContext"
import { getCurrentUser } from "../../../utils/auth"
import { scoresCalculesParLeServeur } from "../../../utils/scoresServeur"

import { TopBar, BottomBar, CenterContainer, LARGEUR_LECTURE, BlocIllustre, useApercuPleineHauteur } from "./FormatsHelpers"
import { APERCU_EDITO } from "./apercu"
import EditableField from "./EditableField"
import EditableButton from "./EditableButton"

/* ⛔ FORMAT « Feedback », porte de l'ancien lecteur le 07/09/2026.
 *
 * POURQUOI. Le lecteur a ete reconstruit a neuf en fevrier 2026 (commit
 * 68be04a) et deux formats de l'ancien n'ont jamais ete reecrits :
 * « Questionnaire » et « Feedback ». Ils tombaient dans le `default` du
 * FormatSwitch, qui rend un fragment vide : un ECRAN BLANC, sans bouton, donc
 * un parcours bloque. Onze modules d'IKOULA et deux modules importes d'AWS
 * (FREE FROM CONFLICT, Communicate From Everywhere) en contiennent.
 *
 * CE QUE FAISAIT L'ANCIEN (OLD/src/components/player/formats/FormatFeedback.js) :
 * un compte de bonnes reponses. Il remonte les sequences precedentes jusqu'au
 * Feedback d'avant, ou parcourt tout le chapitre si `isChapter`, compte les
 * quiz repondus et ceux reussis, et affiche le total sous le titre. C'est
 * l'ancetre du Diagnostic, en plus simple : pas de categories, pas de seuils.
 *
 * On garde exactement cette logique, et on la rend dans le style actuel :
 * titre centre, un chiffre, une phrase. Un quiz est « reussi » quand il
 * rapporte tous ses points, la meme regle que le bilan (getPointsByQuizz).
 * Un quiz sans bonne reponse (noValidAnswer) n'est pas compte. */
const FormatFeedback = ({ actFormation, actIndex, format, setViewPlanCours, viewPlanCours, onlyContent, isEditor }) => {

  const { actLanguage, languageValues } = useContext(LanguageContext)
  const { manageFormatResponse, manageSlide } = useContext(UserContext)

  const [bilan, setBilan] = useState({ quizz: 0, reussis: 0 })

  /* PD-10 (25/09/2026, decision d'Hicham) : un candidat THE SCAN ne voit aucune
     statistique de ses resultats, donc pas ce compte de bonnes reponses
     (« 4 / 6 » et sa phrase). Titre, texte et bouton restent ; le compte est
     toujours calcule et ENREGISTRE comme avant (userChoice = bilan). */
  const masquerBilan = getCurrentUser()?.type === "theScanCandidat" && isEditor !== true
  /* 27/09/2026, chantier « scores cote serveur » : quand le serveur note ce
     candidat (module sans glbMasterPond), les questions arrivent sans points.
     Le compte ci-dessous vaudrait alors toujours « 0 reussi » : l'enregistrer
     mettrait en base un bilan faux. On n'envoie donc que isDone. Avec l'ancien
     serveur, le compte est enregistre comme avant. */
  const bilanParLeServeur = masquerBilan && scoresCalculesParLeServeur(actFormation)

  useEffect(() => {
    const compte = { quizz: 0, reussis: 0 }
    const prendre = (seq) => {
      if (seq?.type !== 'Quizz' || seq?.noValidAnswer === true) return
      if (seq?.userChoice === null || seq?.userChoice === undefined) return
      const [points, max] = getPointsByQuizz(seq)
      compte.quizz += 1
      if (max > 0 && points >= max) compte.reussis += 1
    }
    const chapitre = actFormation?.chapitre?.[actIndex?.chapitre]
    if (format?.isChapter === true) {
      for (const partie of (chapitre?.partie || [])) for (const seq of (partie?.sequence || [])) prendre(seq)
    } else {
      const partie = chapitre?.partie?.[actIndex?.partie]
      for (let i = (actIndex?.sequence ?? 0) - 1; i >= 0; i--) {
        const seq = partie?.sequence?.[i]
        if (seq?.type === 'Feedback') break
        prendre(seq)
      }
    }
    setBilan(compte)
  }, [actFormation, actIndex, format])

  const phrase = () => {
    const d = languageValues?.formation?.feedbackBonnesReponses
    if (!d) return ""
    return d[actLanguage]
      .replace("{n}", bilan.reussis)
      .replace("{total}", bilan.quizz)
  }


  /* ⛔ LA CARTE DE SCORE SUIT LA MEME REGLE (09/09/2026). Hicham : « corrige
     au max pour que ce soit plus simple pour Nawal ». Le feedback n'a jamais
     porte d'image : son contenu restait centre au milieu de l'ecran quand
     celui des ecrans voisins se ferre a gauche.
     L'ancien lecteur ne faisait pas cette difference : `FormatFeedback` y
     rendait deja son contenu dans `MaskQuizz`, le meme panneau que le quiz.
     Le score reste centre DANS sa colonne — c'est un chiffre, il se lit au
     milieu — mais la colonne, elle, se pose au meme endroit que les autres. */
  const pleineHauteur = useApercuPleineHauteur()
  /* Le cote de l'image alterne d'un ecran a l'autre, regulierement. */
  /* Ce format ne porte jamais d'image : ancrage a gauche, sans alternance. */
  const cote = pleineHauteur ? "droite" : null

  return (
    <Box sx={{
      display:"flex",
      flexDirection:"column",
      /* ⚠️ `100vh` vaut la hauteur de la FENETRE, jamais celle d'un conteneur.
         Un apercu qui simule 982 px dans un panneau aurait donc affiche un ecran
         calibre sur la vraie fenetre. La variable `--hs-hauteur-ecran` n'est
         definie QUE par le panneau d'apercu ; partout ailleurs le repli `100vh`
         s'applique et rien ne change. Une variable CSS plutot qu'un prop : neuf
         signatures de composants restent intactes, et un format rendu hors du
         lecteur se comporte correctement sans qu'on ait pense a le lui dire. */
      height:onlyContent?"100%":"var(--hs-hauteur-ecran, 100vh)",
      /* ⛔ LE FORMAT DEVIENT SON PROPRE REFERENTIEL DE MESURE (08/09/2026).
         Nawal voit « de grosses differences » entre l'apercu de l'admin et la
         vue apprenant. Cause : l'apercu ne rend pas l'ecran a la taille de la
         fenetre. `ScaleThumbForm` le rend dans une boite FIXE de 1500 x 1000,
         puis la reduit par un `transform: scale()`. Or `vw` et `vh` se
         calculent sur la FENETRE, jamais sur cette boite : les proportions
         divergeaient donc des deux cotes, sans qu'aucun reglage puisse les
         reconcilier — ce sont deux referentiels differents.
         En declarant ce conteneur, les unites `cqw`/`cqh` employees plus bas se
         mesurent sur LUI. Dans le lecteur il occupe la fenetre : le rendu ne
         bouge pas d'un pixel. Dans l'apercu il fait 1500 x 1000 : le rendu
         devient fidele. */
      containerType:"size",
      overflow:"hidden",
      /* ⛔ PAS DE GAP RACINE (22/09). La planche colle le contenu au bandeau :
         le cadre de contenu commence exactement a y=75, et la bande basse est
         adjacente elle aussi. Ces 10 px repoussaient tout l'ecran vers le bas et
         comptaient pour 10 des 22 px de decalage mesures sur la pastille. */

      /* L'ancre du panneau : sans elle le bloc absolu se cale sur la fenetre. */
      position: APERCU_EDITO ? "relative" : "static",
    }}>
      <TopBar
        actFormation={actFormation}
        actIndex={actIndex}
        setViewPlanCours={setViewPlanCours}
        viewPlanCours={viewPlanCours}
        onlyContent={onlyContent}
      />
      <CenterContainer
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={onlyContent}
        cote={cote}
      >
        <BlocIllustre
          image={null}
          horsFlux={cote}
        >
        <Box sx={{ width:"100%", maxWidth: LARGEUR_LECTURE,
          /* En apercu la colonne se pose a gauche : son contenu suit, sinon
             le score flotterait au milieu d'une colonne ferree. */
          textAlign: pleineHauteur ? "left" : "center",
          /* Ce format n'a pas d'image : largeur de lecture, bord gauche commun. */
          ...(pleineHauteur ? { width:["100%", "100%", "min(52cqw, 720px)", "min(52cqw, 720px)"],
                                maxWidth:"100%", position:"relative", zIndex:1 } : {}),
        }}>
          <EditableField
            field={format?.title}
            type="title"
            isEditor={isEditor}
            sx={{
              color:"text.black",
              /* ⚠️ ECHELLE DU TITRE RELUE DANS FIGMA (21/09/2026, lot 3a).
                   Jeton nomme « Text/Desktop/Heading/H1 » : Questrial Regular,
                   42 px, interligne 1,15, letterSpacing 0. Noeuds 3:302257
                   (Desktop), 3:282076 (Tablet), 3:282135 (Mobile, 26 px / 1,25).
                   Le 46 px du palier lg etait estime : la planche s'arrete a 42.
                   L'interligne n'etait declare NULLE PART, donc le navigateur
                   retombait sur `normal`, soit environ 1,15 a 1,2 selon la
                   police. On le pose explicitement. */
                fontSize:["26px", "42px", "42px", "42px"],
                lineHeight:["1.25", "1.15", "1.15", "1.15"],
              textAlign: pleineHauteur ? "left" : "center",
            }}
          />
          {!masquerBilan && bilan.quizz > 0 &&
            <Box sx={{ marginTop:["24px", "32px", "36px", "40px"] }}>
              <Box sx={{
                color:"text.black",
                fontSize:["48px", "64px", "72px", "80px"],
                lineHeight:1,
                userSelect:"none",
              }}>
                {bilan.reussis}<Box component="span" sx={{ color:"text.subTitle", fontSize:"0.45em" }}> / {bilan.quizz}</Box>
              </Box>
              <Box sx={{
                marginTop:["10px", "14px", "14px", "16px"],
                color:"text.subTitle",
                /* ⚠️ CONSIGNE RELUE DANS FIGMA (21/09/2026, lot 3a). Jeton
                   « Text/Desktop/Body/Large » : 20 px, interligne 1,5, aux deux
                   grands paliers ; 15 px en Mobile (3:282136). Cinq formats
                   portaient cinq echelles differentes pour le MEME element. */
                  fontSize:["15px", "20px", "20px", "20px"],
                  lineHeight:"1.5",
              }}>
                {phrase()}
              </Box>
            </Box>
          }
          {format?.text &&
            <EditableField
              field={format?.text}
              type="text"
              isEditor={isEditor}
              sx={{
                marginTop:["20px", "28px", "28px", "32px"],
                color:"text.black",
                fontSize:["15px", "17px", "17px", "18px"],
                lineHeight:"140%",
                textAlign: pleineHauteur ? "left" : "center",
              }}
            />
          }
        </Box>
        </BlocIllustre>
      </CenterContainer>
      <BottomBar
        actFormation={actFormation}
        actIndex={actIndex}
        onlyContent={isEditor ? false : onlyContent}
        isEditor={isEditor}
        buttons={
          <EditableButton
            isEditor={false}
            type="button"
            /* Regle 1.3 : ce bouton n'enregistre pas de reponse de l'apprenant */
            field={languageValues?.next?.[actLanguage]}
            sx={{}}
            onClick={() => {
              manageFormatResponse(format, bilanParLeServeur
                ? [{ key: 'isDone', value: true }]
                : [{ key: 'isDone', value: true }, { key: 'userChoice', value: bilan }])
              manageSlide(1)
            }}
          />
        }
      />
    </Box>
  )
}

export default FormatFeedback
